import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import {
  COMMISSION_TYPES,
  COMMISSION_TYPE_LABELS,
  PAYOUT_SCHEDULES,
  PAYOUT_SCHEDULE_LABELS,
  PAYOUT_SCHEDULE_DESCRIPTIONS,
  platformChoices,
  PLATFORM_LABELS,
  CATEGORIES,
  PROGRAM_KINDS,
  PROGRAM_KIND_LABELS,
  programFeeBps,
  MAX_RECURRING_MONTHS,
  CURRENCIES,
  CURRENCY_LABELS,
} from '@affily/shared/constants';
import { calculateCommission, describeOffer } from '@affily/shared/commission';
import { DEFAULT_TEMPLATE_KEY, getTemplate, applyTemplate } from '@affily/shared/templates';
import { formatCents, bpsToPercent, percentToBps } from '@affily/shared/money';
import { addYears } from '@affily/shared/programDates';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { toLocalInput, fromLocalInput } from '../../lib/competition.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useBrand } from '../../hooks/useBrand.jsx';
import { useEmailVerification, isEmailUnverifiedError } from '../../hooks/useEmailVerification.js';
import { ConfirmEmailNotice } from '../../components/ConfirmEmailNotice.jsx';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { GiftsEditor, emptyGift, giftToRow, giftsToPayload } from '../../components/GiftsEditor.jsx';
import { Button, Field, Input, Textarea, Select, ChoiceCards, Alert, Badge, Spinner } from '../../components/ui.jsx';
import { TemplateSelect } from '../../components/TemplateSelect.jsx';
import { ProgramSteps } from '../../components/ProgramSteps.jsx';
import { HelpLink } from '../Help.jsx';

/** What each field the server can refuse is called on this page. */
const FIELD_LABELS = {
  title: 'Program title',
  destinationUrl: 'Destination URL',
  description: 'Description',
  visibility: 'Who can see the terms',
  currency: 'Currency',
  kind: 'What does this program pay for?',
  commissionType: 'Commission type',
  rateBps: 'Rate (%)',
  flatAmountCents: 'Amount',
  recurringMode: 'Recurring',
  recurringMonths: 'Billing cycles',
  cookieWindowDays: 'Cookie window (days)',
  payoutSchedule: 'Payout schedule',
  contentRequirements: 'Content requirements',
  categories: 'Categories',
  platformsWanted: 'Platforms',
  averageOrderValueCents: 'Average order value',
  startsAt: 'Starts',
  endsAt: 'Ends',
  paysAfterEnd: 'Pays after the end',
  'intake.mode': 'Creators can apply',
  'intake.opensAt': 'Applications open',
  'intake.closesAt': 'Applications close',
  gifts: 'Gifts',
  competition: 'Competition',
};

/**
 * A refusal as the brand can act on it: every reason, named by its field.
 *
 * Only some fields show their own error inline, so a bare "Validation failed"
 * left the rest (an event, a prize, the payout schedule) with no clue at all.
 */
function refusal(message, details) {
  const reasons = Object.entries(details ?? {}).filter(([, v]) => typeof v === 'string');
  if (reasons.length === 0) return message;
  const label = (key) => {
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];
    const gift = key.match(/^gifts\.(\d+)\./);
    if (gift) return `Gift ${Number(gift[1]) + 1}`;
    return FIELD_LABELS[key.split('.')[0]] ?? key;
  };
  return (
    <>
      <p className="font-medium">This didn’t save:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {reasons.map(([key, reason]) => (
          <li key={key}>
            {label(key)}: {reason}
          </li>
        ))}
      </ul>
    </>
  );
}

const SAVED_MESSAGE = {
  active: 'Published — live in the directory.',
  draft: 'Saved as a draft.',
  paused: 'Paused.',
  closed: 'Closed.',
};

const STATUS_TONE = { active: 'positive', paused: 'caution', draft: 'neutral', closed: 'neutral' };
const STATUS_LABEL = { active: 'Live', paused: 'Paused', draft: 'Draft', closed: 'Closed' };

/** The visible state the editor was previously missing entirely. */
function StatusBadge({ status }) {
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>;
}

const EMPTY = {
  title: '',
  description: '',
  commissionType: 'percent_of_sale',
  ratePercent: '15',
  flatAmount: '',
  cookieWindowDays: '30',
  payoutSchedule: 'net30',
  recurringMode: 'one_time',
  recurringMonths: '12',
  contentRequirements: '',
  categories: [],
  platformsWanted: [],
  destinationUrl: '',
  averageOrderValue: '',
  autoApprove: false,
  gifts: [],
  status: 'draft',
  // New programs default to euros; the brand picks before anyone applies.
  currency: 'eur',
  // What it pays for, which sets Affily's fee on it.
  kind: 'other',
  startsAt: '',
  endsAt: '',
  autoExtend: true,
  visibility: 'public',
  paysAfterEnd: false,
  intakeMode: 'rolling',
  opensAt: '',
  closesAt: '',
};

/** A new program starts now and runs a year, renewing itself unless the brand says otherwise. */
function defaultDates(now = new Date()) {
  return { startsAt: toLocalInput(now), endsAt: toLocalInput(addYears(now, 1)) };
}

/** The form fields a template (or starting from scratch) decides: the terms, and nothing the brand typed. */
const TERM_FIELDS = [
  'kind',
  'commissionType',
  'ratePercent',
  'flatAmount',
  'cookieWindowDays',
  'payoutSchedule',
  'recurringMode',
  'recurringMonths',
  'gifts',
];

/**
 * Form values for a template's terms, or for none.
 *
 * Only the terms: a template carries no title, URL, description or
 * requirements, so picking one never wipes what the brand has already typed.
 */
/** One line under each commission type's box, saying what it pays. */
const COMMISSION_TYPE_HINTS = {
  percent_of_sale: 'A share of each sale',
  flat_per_conversion: 'A fixed amount per sale, booking or lead',
  flat_per_post: 'A fixed fee per approved post or video',
  gift_only: 'Free products, no commission',
};

function termsFor(templateKey) {
  if (!templateKey) {
    return { ...Object.fromEntries(TERM_FIELDS.map((k) => [k, EMPTY[k]])), ...defaultDates(), autoExtend: true };
  }
  const p = applyTemplate(templateKey);
  return {
    // A campaign template brings its own run (three months, not renewing).
    ...(p.startsAt
      ? { startsAt: toLocalInput(p.startsAt), endsAt: toLocalInput(p.endsAt), autoExtend: p.autoExtend !== false }
      : { ...defaultDates(), autoExtend: true }),
    kind: p.kind ?? 'other',
    commissionType: p.commissionType,
    ratePercent: p.rateBps != null ? String(bpsToPercent(p.rateBps)) : '',
    flatAmount: p.flatAmountCents != null ? String(p.flatAmountCents / 100) : '',
    cookieWindowDays: String(p.cookieWindowDays),
    payoutSchedule: p.payoutSchedule,
    recurringMode: p.recurringMode ?? 'one_time',
    recurringMonths: p.recurringMonths != null ? String(p.recurringMonths) : EMPTY.recurringMonths,
    gifts: (p.gifts ?? []).map((g) => ({
      ...emptyGift(),
      name: g.name,
      fulfilment: g.fulfilment,
      retailValue: g.retailValueCents != null ? String(g.retailValueCents / 100) : '',
      quantity: g.quantityTotal != null ? String(g.quantityTotal) : '',
    })),
  };
}

export default function ProgramEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id;

  // ?template=<key> preselects one, so a link from elsewhere (a setup guide, a
  // wizard) can land on a form that is already filled in, and
  // ?template=scratch asks for none. Unknown keys are ignored rather than
  // trusted; without one, a new program starts from the default template.
  const asked = isNew ? searchParams.get('template') : null;
  const templateParam = asked === 'scratch' || getTemplate(asked) ? asked : null;
  const paramKey = templateParam === 'scratch' ? null : templateParam;
  const [templateKey, setTemplateKey] = useState(
    templateParam ? paramKey : isNew ? DEFAULT_TEMPLATE_KEY : null,
  );

  const { data, loading, reload } = useFetch(isNew ? null : '/programs/mine/list', { skip: isNew });
  const [form, setForm] = useState(() => ({ ...EMPTY, ...defaultDates(), ...termsFor(templateKey) }));
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const { verified, refresh } = useEmailVerification();

  const existing = useMemo(
    () => data?.programs?.find((p) => p.id === id) ?? null,
    [data, id],
  );

  useEffect(() => {
    if (!existing) return;
    setForm({
      ...EMPTY,
      ...existing,
      description: existing.description ?? '',
      contentRequirements: existing.contentRequirements ?? '',
      ratePercent: existing.rateBps != null ? String(bpsToPercent(existing.rateBps)) : '',
      flatAmount: existing.flatAmountCents != null ? String(existing.flatAmountCents / 100) : '',
      gifts: (existing.gifts ?? []).map(giftToRow),
      cookieWindowDays: String(existing.cookieWindowDays),
      recurringMode: existing.recurringMode ?? 'one_time',
      recurringMonths: existing.recurringMonths != null ? String(existing.recurringMonths) : '12',
      averageOrderValue:
        existing.averageOrderValueCents != null
          ? String(existing.averageOrderValueCents / 100)
          : '',
      startsAt: toLocalInput(existing.startsAt),
      endsAt: toLocalInput(existing.endsAt),
      autoExtend: existing.autoExtend !== false,
      visibility: existing.visibility ?? 'public',
      paysAfterEnd: Boolean(existing.paysAfterEnd),
      intakeMode: existing.intake?.mode ?? 'rolling',
      opensAt: toLocalInput(existing.intake?.opensAt),
      closesAt: toLocalInput(existing.intake?.closesAt),
    });
  }, [existing]);

  function chooseTemplate(key) {
    setTemplateKey(key);
    setForm((f) => ({ ...f, ...termsFor(key) }));
    setFieldErrors({});
    setSaved(null);
  }

  // Following a link to a different template while this page is already open.
  useEffect(() => {
    if (templateParam && paramKey !== templateKey) chooseTemplate(paramKey);
    // Reacts to the URL only; templateKey changing on its own (a click) must not re-apply it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateParam]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(null);
  };

  const toggleIn = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
    }));

  const isGiftOnly = form.commissionType === 'gift_only';
  const isPercent = form.commissionType === 'percent_of_sale';
  // Whether the chosen type pays money per conversion (and so can recur).
  const recurrable = isPercent || form.commissionType === 'flat_per_conversion';

  /**
   * The conversion actions a template defines, for a NEW program only.
   *
   * Never sent on an edit: conversion actions are managed on their own page,
   * and a save from this form must not overwrite what the brand set up there.
   * Dropped too if the brand has since switched to a different kind of
   * commission than the template was written for — its actions (a payable
   * signup on a gift-only program, say) would no longer describe the deal.
   */
  function templateEvents() {
    const template = isNew && templateKey ? getTemplate(templateKey) : null;
    if (!template?.payload.events || form.commissionType !== template.payload.commissionType) {
      return {};
    }
    const { events, primaryEventKey, competition } = applyTemplate(templateKey, {}, { now: new Date(form.startsAt || Date.now()) });
    // A competition's dates follow the program's, as the brand set them here.
    const dated = competition && {
      ...competition,
      startsAt: fromLocalInput(form.startsAt) ?? competition.startsAt,
      endsAt: fromLocalInput(form.endsAt) ?? competition.endsAt,
    };
    return { events, ...(primaryEventKey ? { primaryEventKey } : {}), ...(dated ? { competition: dated } : {}) };
  }

  function buildPayload() {
    return {
      ...templateEvents(),
      title: form.title,
      description: form.description || undefined,
      commissionType: form.commissionType,
      // A gift-only program has no rate or amount, and a stale fixed_period
      // left over from before the switch would be rejected as unable to recur.
      rateBps: isPercent ? percentToBps(Number(form.ratePercent || 0)) : null,
      flatAmountCents:
        isPercent || isGiftOnly ? null : Math.round(Number(form.flatAmount || 0) * 100),
      cookieWindowDays: Number(form.cookieWindowDays),
      payoutSchedule: form.payoutSchedule,
      recurringMode: isGiftOnly ? 'one_time' : form.recurringMode,
      recurringMonths:
        !isGiftOnly && form.recurringMode === 'fixed_period'
          ? Number(form.recurringMonths || 0)
          : null,
      gifts: giftsToPayload(form.gifts),
      contentRequirements: form.contentRequirements || undefined,
      categories: form.categories,
      platformsWanted: form.platformsWanted,
      destinationUrl: form.destinationUrl,
      averageOrderValueCents: form.averageOrderValue
        ? Math.round(Number(form.averageOrderValue) * 100)
        : null,
      autoApprove: form.autoApprove,
      currency: form.currency,
      kind: form.kind,
      startsAt: fromLocalInput(form.startsAt) ?? undefined,
      endsAt: fromLocalInput(form.endsAt) ?? undefined,
      autoExtend: form.autoExtend,
      visibility: form.visibility,
      paysAfterEnd: form.paysAfterEnd,
      intake:
        form.intakeMode === 'window'
          ? { mode: 'window', opensAt: fromLocalInput(form.opensAt), closesAt: fromLocalInput(form.closesAt) }
          : { mode: 'rolling' },
      status: form.status,
    };
  }

  /** `next` goes on to step 2, tracking, once the save went through. */
  async function save(status, { next = false } = {}) {
    // A second click while the first is in flight is what produced four
    // identical PATCHes in the logs.
    if (busy) return;

    // The buttons are disabled, but Enter in a field submits the form. Going
    // live needs a confirmed email; everything else is fine.
    if (status === 'active' && existing?.status !== 'active' && !verified) {
      setError('Confirm your email address before publishing — you can save a draft in the meantime.');
      return;
    }

    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const payload = { ...buildPayload(), status };
      if (isNew) {
        const { program } = await api.post('/programs', payload);
        // Saved terms are half a program: nothing is credited until sales reach
        // Affily, so a new one goes straight to its tracking.
        navigate(
          `/dashboard/brand/programs/${program.id}/actions?from=${status === 'active' ? 'published' : 'created'}`,
        );
      } else {
        await api.patch(`/programs/${id}`, payload);
        if (next) {
          navigate(`/dashboard/brand/programs/${id}/actions`);
          return;
        }
        // Going live is when creators' links start sending traffic: confirm
        // where it lands and that sales are reported before they do.
        if (status === 'active' && existing?.status !== 'active') {
          navigate(`/dashboard/brand/programs/${id}/actions?from=published`);
          return;
        }
        setForm((f) => ({ ...f, status }));
        // Without this the page keeps rendering the data it fetched on mount,
        // so a successful save changed nothing on screen — which is exactly
        // what "the buttons flicker and nothing happens" looked like.
        await reload();
        setSaved(status);
      }
    } catch (err) {
      // The server's verdict wins: the email may have been confirmed or
      // un-confirmed since this page loaded. `details` here is a code, not
      // per-field errors, so keep it out of the form.
      if (isEmailUnverifiedError(err)) {
        setError(err.message);
        refresh().catch(() => {});
      } else {
        setError(refusal(err.message, err.details));
        setFieldErrors(err.details ?? {});
      }
    } finally {
      setBusy(false);
    }
  }

  if (!isNew && loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      side="brand"
      title={isNew ? 'New program' : 'Edit program'}
      description="A public program’s terms are open to everyone the moment you publish, so creators can compare them before they apply. A private one’s go only to the creators you invite or accept."
      actions={
        existing && (
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={form.status} />
            <Button to={`/programs/${existing.slug}`} variant="outline" size="sm">
              View public page
            </Button>
          </div>
        )
      }
    >
      <ProgramSteps programId={existing?.id} current="setup" program={existing} />
      {isNew && <TemplatePicker selected={templateKey} onChoose={chooseTemplate} />}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          // Enter does what the main button does: a draft saves and moves on to
          // tracking (publishing is step 4); a live program saves its changes.
          if (form.status === 'draft') save('draft', { next: true });
          else save(form.status);
        }}
        className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div className="space-y-5">
          <Alert>{error}</Alert>

          <Field label="Program title" required error={fieldErrors.title} constraint={rule.max(140)}>
            <Input
              value={form.title}
              onChange={set('title')}
              required
              maxLength={140}
              placeholder="Skincare affiliate program"
            />
          </Field>

          <Field
            label="Destination URL"
            required
            error={fieldErrors.destinationUrl}
            constraint={rule.url()}
            hint="Where a tracking link sends the visitor."
          >
            <Input
              type="url"
              value={form.destinationUrl}
              onChange={set('destinationUrl')}
              required
              placeholder="https://example.com/shop"
            />
          </Field>

          <Field label="Description" error={fieldErrors.description} constraint={rule.max(4000)}>
            <Textarea value={form.description} onChange={set('description')} rows={4} maxLength={4000} />
          </Field>

          <ChoiceCards
            label="Who can see the terms"
            name="program-visibility"
            error={fieldErrors.visibility}
            value={form.visibility}
            onChange={set('visibility')}
            options={[
              {
                value: 'public',
                title: 'Everyone',
                description: 'Listed in the directory, with the terms open to everyone.',
              },
              {
                value: 'private',
                title: 'Only creators I invite or accept',
                description:
                  'Left out of the directory, your brand page and search. Anyone with the link can apply; the terms go to creators you invite or accept.',
              },
            ]}
          />

          <ProgramDates
            form={form}
            set={set}
            fieldErrors={fieldErrors}
            locked={Boolean(existing?.hasCreators)}
            paysAfterEndLocked={Boolean(existing?.hasCreators && existing.paysAfterEnd)}
          />

          <fieldset className="rounded-lg border border-rule bg-paper-raised p-4">
            <legend className="px-1 text-sm font-semibold">
              Terms · {form.visibility === 'private' ? 'shared with invited and accepted creators' : 'published publicly'}
            </legend>

            <div className="mt-2 space-y-4">
              <ChoiceCards
                label="Currency"
                required
                columns={3}
                error={fieldErrors.currency}
                value={form.currency}
                onChange={set('currency')}
                disabled={Boolean(existing?.hasApplicants)}
                options={CURRENCIES.map((c) => ({ value: c, title: CURRENCY_LABELS[c] }))}
                hint={
                  existing?.hasApplicants
                    ? 'Fixed now that creators have applied: everything on this program is paid in it.'
                    : (
                        <>
                          Every commission, prize and payout on this program is in this currency.{' '}
                          <HelpLink slug="which-currencies">About currencies</HelpLink>
                        </>
                      )
                }
              />

              <ChoiceCards
                label="What does this program pay for?"
                required
                error={fieldErrors.kind}
                value={form.kind}
                onChange={set('kind')}
                disabled={Boolean(existing?.hasApplicants)}
                options={PROGRAM_KINDS.map((k) => {
                  // "Digital products: courses, e-books…" → a title and its examples.
                  const [title, ...rest] = PROGRAM_KIND_LABELS[k].split(': ');
                  return { value: k, title, description: rest.join(': ') || undefined };
                })}
                hint={
                  existing?.hasApplicants
                    ? 'Fixed now that creators have applied.'
                    : 'Sets Affily’s fee on its commissions; see your Pricing page.'
                }
              />

              <ChoiceCards
                label="Commission type"
                required
                columns={4}
                value={form.commissionType}
                onChange={set('commissionType')}
                options={COMMISSION_TYPES.map((t) => ({
                  value: t,
                  title: COMMISSION_TYPE_LABELS[t],
                  description: COMMISSION_TYPE_HINTS[t],
                }))}
              />

              {isGiftOnly ? (
                <p className="rounded-md border border-rule bg-paper-sunken px-3 py-2 text-sm text-ink-muted">
                  No commission. This program pays only in the gifts below — conversions are still
                  tracked so you can see what they drove, but nothing accrues and nothing is paid
                  out.
                </p>
              ) : isPercent ? (
                <Field label="Rate (%)" required error={fieldErrors.rateBps} constraint={rule.percent()}>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.ratePercent}
                    onChange={set('ratePercent')}
                    required
                  />
                </Field>
              ) : (
                <Field
                  label={`Amount per ${form.commissionType === 'flat_per_post' ? 'post' : 'conversion'} (${form.currency.toUpperCase()})`}
                  required
                  error={fieldErrors.flatAmountCents}
                  constraint={rule.money()}
                >
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.flatAmount}
                    onChange={set('flatAmount')}
                    required
                  />
                </Field>
              )}

              {recurrable ? (
                <div className="rounded-md border border-rule bg-paper-sunken p-3">
                  <ChoiceCards
                    label="Payout model"
                    value={form.recurringMode}
                    onChange={set('recurringMode')}
                    options={[
                      { value: 'one_time', title: 'One-time', description: 'Paid once per conversion' },
                      {
                        value: 'fixed_period',
                        title: 'Recurring',
                        description: 'Paid again on each renewal, for a set number of billing cycles',
                      },
                    ]}
                  />

                  {form.recurringMode === 'fixed_period' && (
                    <Field
                      label="Billing cycles"
                      className="mt-3"
                      required
                      error={fieldErrors.recurringMonths}
                      constraint={rule.recurringCycles()}
                      hint="Including the first. A perpetual claim on your revenue is closer to equity than a commission, which is why there is no lifetime option."
                    >
                      <Input
                        type="number"
                        min="1"
                        max={MAX_RECURRING_MONTHS}
                        value={form.recurringMonths}
                        onChange={set('recurringMonths')}
                        required
                      />
                    </Field>
                  )}
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Cookie window (days)"
                  required
                  error={fieldErrors.cookieWindowDays}
                  constraint={rule.cookieWindow()}
                  hint="How long a click stays creditable."
                >
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={form.cookieWindowDays}
                    onChange={set('cookieWindowDays')}
                    required
                  />
                </Field>

              </div>

              {!isGiftOnly && (
                <ChoiceCards
                  label="Payout schedule"
                  required
                  columns={4}
                  value={form.payoutSchedule}
                  onChange={set('payoutSchedule')}
                  options={PAYOUT_SCHEDULES.map((p) => ({
                    value: p,
                    title: PAYOUT_SCHEDULE_LABELS[p],
                    description: PAYOUT_SCHEDULE_DESCRIPTIONS[p],
                  }))}
                  hint="A promise you publish, not something Affily enforces — you release each payout yourself. Creators compare it across programs and can review you publicly afterwards, so pick a window you will actually keep. A shorter one is a real advantage when a creator is choosing between you and someone paying Net 60."
                />
              )}

              <Field
                label={`Average order value (${form.currency.toUpperCase()})`}
                constraint={rule.money()}
                hint="Optional, but creators use it to judge whether the rate is worth their time."
              >
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.averageOrderValue}
                  onChange={set('averageOrderValue')}
                />
              </Field>
            </div>
          </fieldset>

          <GiftsEditor
            gifts={form.gifts}
            onChange={(gifts) => {
              setForm((f) => ({ ...f, gifts }));
              setSaved(null);
            }}
            fieldErrors={fieldErrors}
            giftOnly={isGiftOnly}
            currency={form.currency}
          />

          <Field
            label="Content requirements"
            error={fieldErrors.contentRequirements}
            constraint={rule.max(4000)}
            hint="Disclosure rules, claims you can’t make, anything a creator must know up front."
          >
            <Textarea
              value={form.contentRequirements}
              onChange={set('contentRequirements')}
              rows={4}
              maxLength={4000}
            />
          </Field>

          <fieldset>
            <legend className="mb-1.5 text-sm font-medium">Categories</legend>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => toggleIn('categories', c)}>
                  <Badge tone={form.categories.includes(c) ? 'accent' : 'neutral'} className="capitalize">
                    {c}
                  </Badge>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-1.5 text-sm font-medium">Platforms wanted</legend>
            <div className="flex flex-wrap gap-1.5">
              {platformChoices(form.platformsWanted).map((p) => (
                <button key={p} type="button" onClick={() => toggleIn('platformsWanted', p)}>
                  <Badge tone={form.platformsWanted.includes(p) ? 'accent' : 'neutral'}>
                    {PLATFORM_LABELS[p]}
                  </Badge>
                </button>
              ))}
            </div>
          </fieldset>

          <label className="flex items-start gap-2.5 rounded-lg border border-rule bg-paper-raised p-3">
            <input
              type="checkbox"
              checked={form.autoApprove}
              onChange={set('autoApprove')}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              <span className="block text-sm font-medium">Auto-approve applications</span>
              <span className="block text-xs text-ink-muted">
                Creators start immediately instead of waiting. Shown on the public page either way.
              </span>
            </span>
          </label>

          <div className="border-t border-rule pt-5">
            <div className="flex flex-wrap items-center gap-3">
              {form.status === 'draft' ? (
                <>
                  {/* Publishing is step 4, after tracking and invites. */}
                  <Button type="button" onClick={() => save('draft', { next: true })} disabled={busy}>
                    {busy ? 'Saving…' : 'Save and continue →'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => save('draft')}
                    disabled={busy}
                  >
                    Save as draft
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" onClick={() => save(form.status)} disabled={busy}>
                    {busy ? 'Saving…' : 'Save changes'}
                  </Button>
                  {form.status === 'active' && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => save('paused')}
                      disabled={busy}
                    >
                      Pause
                    </Button>
                  )}
                  {form.status === 'paused' && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => save('active')}
                      disabled={busy || !verified}
                    >
                      Resume
                    </Button>
                  )}
                </>
              )}

              {/* Beside the button that was pressed, not only at the top of a
                  form the reader has probably scrolled past. */}
              {saved && !busy && (
                <span className="text-sm font-medium text-positive" role="status">
                  {SAVED_MESSAGE[saved] ?? 'Saved.'}
                </span>
              )}
            </div>

            {/* Again by the buttons: the one at the top is off screen from here. */}
            <Alert className="mt-3">{error}</Alert>

            {form.status === 'paused' && <ConfirmEmailNotice className="mt-3" />}

            <p className="mt-2 text-xs text-ink-faint">
              {form.status === 'draft'
                ? 'A draft is invisible: not in the directory, not in search, and its page 404s. You publish it in step 4.'
                : form.status === 'active'
                  ? 'Live in the directory. Creators can see these terms and apply.'
                  : 'Paused — the terms stay public, but nobody new can apply.'}
            </p>
          </div>
        </div>

        <CommissionPreview form={form} isPercent={isPercent} programId={existing?.id} />
      </form>

      {existing && <MoveOrDuplicate program={existing} />}
    </DashboardLayout>
  );
}

/**
 * Move this program to another of the account's brands (only before anyone
 * has applied, see the server), or copy its terms into a new draft.
 */
function MoveOrDuplicate({ program }) {
  const navigate = useNavigate();
  const { brands, activeBrandId, switchBrand } = useBrand();
  const others = brands.filter((b) => b.id !== activeBrandId);
  const [target, setTarget] = useState(others[0]?.id ?? activeBrandId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const targetName = brands.find((b) => b.id === target)?.companyName;
  const sameBrand = target === activeBrandId;

  async function act(action) {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(
        `/programs/${program.id}/${action}`,
        action === 'move' || !sameBrand ? { brandId: target } : {},
      );
      // The program is now the other brand's: follow it there.
      if (!sameBrand) await switchBrand(target);
      navigate(`/dashboard/brand/programs/${res.program.id}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <section className="mt-12 max-w-2xl border-t border-rule pt-6">
      <h2 className="text-sm font-semibold">Move or duplicate</h2>
      <p className="mt-1 text-xs text-ink-faint">
        Made it under the wrong brand? Move it, as long as no creator has applied yet. Duplicate
        copies the terms, events and gifts into a new draft, for this brand or another.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {brands.length > 1 && (
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label="Brand"
            className="max-w-64"
          >
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.companyName}
                {b.id === activeBrandId ? ' (this brand)' : ''}
              </option>
            ))}
          </Select>
        )}
        {!sameBrand && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => act('move')}>
            Move to {targetName}
          </Button>
        )}
        <Button variant="outline" size="sm" disabled={busy} onClick={() => act('duplicate')}>
          {sameBrand ? 'Duplicate' : `Duplicate to ${targetName}`}
        </Button>
      </div>
      <Alert className="mt-3">{error}</Alert>
    </section>
  );
}

/**
 * "What are you selling?" — filled-in terms for the common kinds of deal.
 *
 * Choosing one only sets the terms; the form below stays fully editable, and
 * nothing is saved or published until the brand does that themselves.
 */
function TemplatePicker({ selected, onChoose }) {
  const chosen = getTemplate(selected);

  return (
    <section className="mb-8 max-w-4xl" aria-label="Program template">
      <TemplateSelect value={selected} onChange={onChoose} scratchBlurb="Set every term yourself." />
      {chosen?.payload.events && (
        <p className="mt-2 text-xs text-ink-muted">
          Also sets up these conversion actions:{' '}
          {chosen.payload.events.map((e) => e.name).join(', ')}. You can change them after saving.
        </p>
      )}
    </section>
  );
}

/**
 * Live commission preview.
 *
 * Computed by the same `calculateCommission` the tracking engine calls when a
 * conversion actually lands — not a lookalike. What a brand sees here is what it
 * will pay, to the cent: the commission plus its share of Affily's fee.
 */
function CommissionPreview({ form, isPercent, programId }) {
  const isGiftOnly = form.commissionType === 'gift_only';
  const money = (cents) => formatCents(cents, { currency: form.currency });
  const terms = {
    currency: form.currency,
    commissionType: form.commissionType,
    rateBps: isPercent ? percentToBps(Number(form.ratePercent || 0)) : null,
    flatAmountCents:
      isPercent || isGiftOnly ? null : Math.round(Number(form.flatAmount || 0) * 100),
    cookieWindowDays: Number(form.cookieWindowDays || 0),
    recurringMode: isGiftOnly ? 'one_time' : form.recurringMode,
    recurringMonths:
      !isGiftOnly && form.recurringMode === 'fixed_period' ? Number(form.recurringMonths || 0) : null,
  };

  // Only gifts with a name count, so a half-typed row doesn't flash into the
  // headline.
  const previewGifts = form.gifts
    .filter((g) => g.name.trim())
    .map((g) => ({ name: g.name.trim(), isActive: true }));

  const sampleOrderCents = form.averageOrderValue
    ? Math.round(Number(form.averageOrderValue) * 100)
    : 10_000;

  let breakdown = null;
  let summary = '—';
  try {
    // No fee maths for a program that pays no money.
    breakdown = isGiftOnly
      ? null
      : calculateCommission(terms, { orderAmountCents: sampleOrderCents, feeBps: programFeeBps(form.kind) });
    summary = describeOffer(terms, previewGifts);
  } catch {
    // Mid-typing the form is briefly invalid. Show a dash rather than an error.
  }

  return (
    <aside className="lg:sticky lg:top-8 lg:self-start">
      <div className="rounded-lg border border-rule bg-paper-raised p-4">
        <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
          What a creator sees
        </h2>
        <p className="mt-2 text-2xl font-semibold tabular-nums text-accent">{summary}</p>
        <p className="mt-1 text-xs text-ink-muted">
          {terms.cookieWindowDays || 0}-day cookie
          {!isGiftOnly &&
            ` · ${PAYOUT_SCHEDULE_LABELS[form.payoutSchedule] ?? form.payoutSchedule}`}
        </p>

        {breakdown && (
          <>
            <div className="mt-4 border-t border-rule pt-3">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                {form.commissionType === 'flat_per_post'
                  ? 'Per approved post'
                  : `On a ${money(sampleOrderCents)} order`}
              </p>
              <table className="mt-2 w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-1 text-ink-muted">Your commission</td>
                    <td className="py-1 text-right font-medium tabular-nums">
                      {money(breakdown.grossAmountCents)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-ink-muted">Fee</td>
                    <td className="py-1 text-right tabular-nums text-ink-muted">
                      +{money(breakdown.brandFeeCents)}
                    </td>
                  </tr>
                  <tr className="border-t border-rule">
                    <td className="py-1.5 font-medium">You pay</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">
                      {money(breakdown.grossAmountCents + breakdown.brandFeeCents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-ink-faint">
              Computed by the same function that pays the commission. Paying by card adds the card
              fee; a bank transfer adds none.
            </p>
          </>
        )}

        {form.recurringMode === 'fixed_period' && breakdown && Number(form.recurringMonths) > 0 && (
          <p className="mt-3 border-t border-rule pt-3 text-xs text-ink-faint">
            Over the full {form.recurringMonths}-cycle window that is{' '}
            <span className="font-medium text-ink">
              {money(breakdown.grossAmountCents * Number(form.recurringMonths))}
            </span>{' '}
            per customer — a forward commitment, not a one-off cost. Worth checking against your own
            churn before you publish it.
          </p>
        )}

        {programId && !isGiftOnly && (
          <p className="mt-3 border-t border-rule pt-3 text-xs text-ink-faint">
            Want to pay for signups or trials too?{' '}
            <Link
              to={`/dashboard/brand/programs/${programId}/actions`}
              className="text-accent hover:underline"
            >
              Set up conversion actions
            </Link>
          </p>
        )}

        <p className="mt-4 border-t border-rule pt-3 text-xs text-ink-faint">
          Editing these terms later never changes commissions already earned — each partnership
          keeps a frozen copy from the day it was accepted.{' '}
          <Link to="/transparency" className="text-accent hover:underline">
            How that works
          </Link>
        </p>
      </div>
    </aside>
  );
}

/**
 * When the program runs and how creators join. Every program has an end; most
 * renew themselves a year at a time. Once creators have joined, the dates can
 * only move in their favour — the server enforces it, this says so up front.
 */
function ProgramDates({ form, set, fieldErrors, locked, paysAfterEndLocked }) {
  return (
    <fieldset className="rounded-lg border border-rule bg-paper-raised p-4">
      <legend className="px-1 text-sm font-semibold">Dates and applications · published publicly</legend>
      <p className="text-xs text-ink-muted">
        <HelpLink slug="when-does-a-program-end">What happens when a program ends</HelpLink>
      </p>

      <div className="mt-2 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Starts"
            required
            error={fieldErrors.startsAt}
            hint={locked ? 'Creators have joined: it can only move earlier.' : 'Clicks and actions count from here.'}
          >
            <Input type="datetime-local" value={form.startsAt} onChange={set('startsAt')} required />
          </Field>
          <Field
            label="Ends"
            required
            error={fieldErrors.endsAt}
            hint={locked ? 'Creators have joined: it can only move later.' : 'Nothing new is credited after this.'}
          >
            <Input type="datetime-local" value={form.endsAt} onChange={set('endsAt')} required />
          </Field>
        </div>

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={form.autoExtend}
            onChange={set('autoExtend')}
            className="mt-0.5 size-4 accent-accent"
          />
          <span>
            <span className="block text-sm font-medium">Extend by a year automatically</span>
            <span className="block text-xs text-ink-muted">
              The end date moves on a year whenever it arrives, until you turn this off. Turn it off
              for a campaign that should simply stop.
            </span>
          </span>
        </label>

        {/* Only an ending program has an "after": a renewing one never ends. */}
        {!form.autoExtend && (
          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              checked={form.paysAfterEnd}
              onChange={set('paysAfterEnd')}
              disabled={paysAfterEndLocked}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              <span className="block text-sm font-medium">Keep paying after the program ends</span>
              <span className="block text-xs text-ink-muted">
                {paysAfterEndLocked
                  ? 'Creators joined on terms that pay after the end, so this stays on.'
                  : 'For sales you record from actions during the program, and renewals of subscriptions sold during it. Off, nothing is paid after the end. Once creators join, this can be turned on but not off.'}
              </span>
              {fieldErrors.paysAfterEnd && (
                <span className="block text-xs text-negative">{fieldErrors.paysAfterEnd}</span>
              )}
            </span>
          </label>
        )}

        <ChoiceCards
          label="Creators can apply"
          error={fieldErrors['intake.mode']}
          value={form.intakeMode}
          onChange={set('intakeMode')}
          options={[
            { value: 'rolling', title: 'Any time', description: 'Until the program ends' },
            { value: 'window', title: 'During a window', description: 'Applications open and close before it starts' },
          ]}
        />

        {form.intakeMode === 'window' && (
          <div className="grid gap-4 rounded-md border border-rule bg-paper-sunken p-3 sm:grid-cols-2">
            <Field label="Applications open" required error={fieldErrors['intake.opensAt']}>
              <Input type="datetime-local" value={form.opensAt} onChange={set('opensAt')} required />
            </Field>
            <Field
              label="Applications close"
              required
              error={fieldErrors['intake.closesAt']}
              hint="By the start date. You can still invite creators until the program ends."
            >
              <Input type="datetime-local" value={form.closesAt} onChange={set('closesAt')} required />
            </Field>
          </div>
        )}
      </div>
    </fieldset>
  );
}
